<template>
  <v-container fluid pa-0>
    <v-row v-if="!openGroupId" justify="center" class="pa-6">
      <v-alert type="info" border="left" colored-border elevation="1">
        Save the open group first to manage departure dates.
      </v-alert>
    </v-row>

    <template v-else>
      <v-row align="center" class="pa-2">
        <v-col class="py-0">
          <list-filters
            :filters="filters"
            label="Find Departure"
            ref-name="openGroupDeparturesListFilters"
            @searchParamsChanged="searchParamsChanged($event)"
            :expansion-panel="false"
          ></list-filters>
        </v-col>
        <div class="mr-2">
          <v-btn color="primary" @click="openAdd">
            <v-icon left>mdi-plus</v-icon>
            Add Departure
          </v-btn>
        </div>
        <div>
          <list-visible-columns-and-filter
            :visible-columns="visibleColumns"
            :selectable-headers="selectableHeaders"
            v-model="search"
            @change="onVisibleColumnsChange"
          ></list-visible-columns-and-filter>
        </div>
      </v-row>

      <v-data-table
        :headers="filteredHeaders"
        :items="items"
        :loading="$apollo.loading"
        v-bind="pagingOptions"
        :search="search"
        item-key="id"
        class="elevation-1"
        fixed-header
        hide-default-footer
        @update:options="onListOptionsUpdate"
        :sort-by="sortBy"
        :sort-desc="sortDesc"
      >
        <template v-slot:item.startDate="{ item }">
          {{ item.startDate | formatDate }}
        </template>
        <template v-slot:item.returnDate="{ item }">
          {{ item.returnDate | formatDate }}
        </template>
        <template v-slot:item.roomsAvailability="{ item }">
          <v-tooltip bottom v-if="item.roomsAvailability">
            <template v-slot:activator="{ on }">
              <span
                v-on="on"
                :class="{ 'red--text font-weight-bold': item.roomsRemaining < 0 }"
              >
                {{ item.roomsAvailability }}
              </span>
            </template>
            <span>Available / total rooms per room type</span>
          </v-tooltip>
        </template>
        <template v-slot:item.actions="{ item }">
          <default-list-actions
            :item="item"
            :hide-view="true"
            @editItem="editItem"
            @deleteItem="deleteItem"
          >
            <template slot="appendButton">
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <v-btn
                    v-on="on"
                    icon
                    small
                    class="mx-0"
                    @click="openBook(item)"
                  >
                    <v-icon color="primary" small>mdi-airplane-takeoff</v-icon>
                  </v-btn>
                </template>
                <span>Create Itinerary</span>
              </v-tooltip>
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <v-btn
                    v-on="on"
                    icon
                    small
                    class="mx-0"
                    @click="openBookedList(item)"
                  >
                    <v-icon color="primary" small>mdi-format-list-bulleted</v-icon>
                  </v-btn>
                </template>
                <span>Booked Itineraries</span>
              </v-tooltip>
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <v-btn
                    v-on="on"
                    icon
                    small
                    class="mx-0"
                    @click="openCopy(item)"
                  >
                    <v-icon color="primary" small>mdi-content-copy</v-icon>
                  </v-btn>
                </template>
                <span>Copy to Date</span>
              </v-tooltip>
            </template>
          </default-list-actions>
        </template>
      </v-data-table>

      <get-more-results
        :first="first"
        @limitChange="onLimitChange"
        :items="openGroupDepartures"
        :totalItems="totalItems"
        :loading="$apollo.loading"
        :loadMoreEnabled="loadMoreEnabled"
        @loadMore="loadMore('openGroupDepartures')"
      ></get-more-results>

      <open-group-departure-copy-dialog
        v-model="copyDialog"
        :source="copySource"
        :master-valid-from="masterValidFrom"
        :master-valid-to="masterValidTo"
        :master-days-of-week="masterDaysOfWeek"
        @copied="onCopied"
      ></open-group-departure-copy-dialog>

      <open-group-departure-add-dialog
        :show="addDialog"
        :open-group-id="openGroupId"
        :master-name="masterName"
        :master-adults="masterAdults"
        :master-children="masterChildren"
        :master-duration="masterDuration"
        :master-valid-from="masterValidFrom"
        :master-valid-to="masterValidTo"
        :master-days-of-week="masterDaysOfWeek"
        @created="onCreated"
        @close="addDialog = false"
      ></open-group-departure-add-dialog>

      <open-group-departure-edit-dialog
        :show="editDialog"
        :departure="editTarget"
        :master-valid-from="masterValidFrom"
        :master-valid-to="masterValidTo"
        @updated="onUpdated"
        @close="editDialog = false"
      ></open-group-departure-edit-dialog>

      <open-group-departure-book-dialog
        :show="bookDialog"
        :departure="bookTarget"
        @created="onBooked"
        @close="bookDialog = false"
      ></open-group-departure-book-dialog>

      <open-group-departure-booked-list-dialog
        :show="bookedListDialog"
        :departure="bookedListTarget"
        @close="bookedListDialog = false"
      ></open-group-departure-booked-list-dialog>
    </template>
  </v-container>
</template>

<script>
import { ListMixin } from "../../../../mixins/ListMixin";
import { removeGQLTypename } from "../../../../helpers";
import GetMoreResults from "../../shared/GetMoreResults";
import DefaultListActions from "../../shared/DefaultListActions";
import ListFilters from "../../shared/list-filters/ListFilters";
import ListVisibleColumnsAndFilter from "../../shared/ListVisibleColumnsAndFilter";
import departureFilters from "../open-group-departure-filters";
import {
  OPEN_GROUP_DEPARTURES_QUERY,
  DELETE_OPEN_GROUP_DEPARTURE,
} from "../departure-gql";
import OpenGroupDepartureCopyDialog from "./OpenGroupDepartureCopyDialog";
import OpenGroupDepartureAddDialog from "./OpenGroupDepartureAddDialog";
import OpenGroupDepartureEditDialog from "./OpenGroupDepartureEditDialog";
import OpenGroupDepartureBookDialog from "./OpenGroupDepartureBookDialog";
import OpenGroupDepartureBookedListDialog from "./OpenGroupDepartureBookedListDialog";

export default {
  name: "OpenGroupDeparturesTab",

  props: {
    openGroupId: { type: String, default: null },
    masterName: { type: String, default: "" },
    masterAdults: { type: Number, default: 0 },
    masterChildren: { type: Number, default: 0 },
    masterDuration: { type: Number, default: 7 },
    masterValidFrom: { type: String, default: null },
    masterValidTo: { type: String, default: null },
    masterDaysOfWeek: { type: Array, default: () => [] },
    refName: {
      type: String,
      default: () => "open-group-departures-tab",
    },
  },

  mixins: [ListMixin],

  components: {
    GetMoreResults,
    DefaultListActions,
    ListFilters,
    ListVisibleColumnsAndFilter,
    OpenGroupDepartureCopyDialog,
    OpenGroupDepartureAddDialog,
    OpenGroupDepartureEditDialog,
    OpenGroupDepartureBookDialog,
    OpenGroupDepartureBookedListDialog,
  },

  data() {
    return {
      openGroupDepartures: {},
      search: "",
      copyDialog: false,
      copySource: null,
      addDialog: false,
      editDialog: false,
      editTarget: null,
      bookDialog: false,
      bookTarget: null,
      bookedListDialog: false,
      bookedListTarget: null,
      visibleColumns: this.$store.getters.localSettings(this.$options.name, [
        "startDate",
        "returnDate",
        "name",
        "adults",
        "usedAdults",
        "remainingAdults",
        "children",
        "usedChildren",
        "remainingChildren",
        "roomsAvailability",
        "actions",
      ]),
      headers: [
        { text: "Start Date", value: "startDate", name: "startDate" },
        { text: "Return Date", value: "returnDate", name: "returnDate" },
        { text: "Name", value: "name", name: "name" },
        { text: "Adults", value: "adults", name: "adults" },
        { text: "Used Adults", value: "usedAdults", name: "usedAdults" },
        { text: "Remaining Adults", value: "remainingAdults", name: "remainingAdults" },
        { text: "Children", value: "children", name: "children" },
        { text: "Used Children", value: "usedChildren", name: "usedChildren" },
        { text: "Remaining Children", value: "remainingChildren", name: "remainingChildren" },
        // #11180 — one column, remaining/total rooms per accommodation item
        {
          text: "Rooms Qty Available/Total",
          value: "roomsAvailability",
          name: "roomsAvailability",
          sortable: false,
        },
        {
          text: "Actions",
          value: "actions",
          sortable: false,
          name: "actions",
          align: "center",
        },
      ],
    };
  },

  computed: {
    items() {
      return this.openGroupDepartures && this.openGroupDepartures.edges
        ? this.openGroupDepartures.edges.map(removeGQLTypename)
        : [];
    },
    filters() {
      return departureFilters.map((f) => {
        f.props.outlined = this.$store.getters.localSettings("outlinedInputs");
        return f;
      });
    },
    scopedFilter() {
      return {
        ...(this.searchParams || {}),
        openGroupId: this.openGroupId,
      };
    },
  },

  methods: {
    editItem(item) {
      this.editTarget = item;
      this.editDialog = true;
    },
    onUpdated(dep) {
      this.$store.dispatch("showAppSnackbar", {
        msg: `Departure on ${this.$options.filters.formatDate(
          dep.startDate
        )} updated.`,
      });
      this.$apollo.queries.openGroupDepartures.refetch();
    },
    deleteItem(item) {
      this.$apollo
        .mutate({
          mutation: DELETE_OPEN_GROUP_DEPARTURE,
          variables: { id: item.id },
        })
        .then(() => {
          this.$store.dispatch("showAppSnackbar", {
            msg: `Departure on ${this.$options.filters.formatDate(item.startDate)} removed.`,
          });
          this.$apollo.queries.openGroupDepartures.refetch();
        })
        .catch((err) => {
          const msg = err.graphQLErrors
            ? err.graphQLErrors.map((e) => e.message).join(", ")
            : err.message;
          this.$store.dispatch("showAppSnackbar", { msg, color: "error" });
        });
    },
    openCopy(item) {
      this.copySource = item;
      this.copyDialog = true;
    },
    onCopied(newDep) {
      this.$store.dispatch("showAppSnackbar", {
        msg: `Copied to ${this.$options.filters.formatDate(newDep.startDate)}.`,
      });
      this.$apollo.queries.openGroupDepartures.refetch();
    },
    openAdd() {
      this.addDialog = true;
    },
    openBook(item) {
      this.bookTarget = item;
      this.bookDialog = true;
    },
    onBooked(itinerary) {
      this.bookDialog = false;
      this.$store.dispatch("showAppSnackbar", {
        msg: `Itinerary ${itinerary.reference || ""} created.`,
      });
      this.$router.push(`/itinerary/${itinerary.id}/details`);
    },
    openBookedList(item) {
      this.bookedListTarget = item;
      this.bookedListDialog = true;
    },
    onCreated(res) {
      if (res.kind === "single") {
        this.$store.dispatch("showAppSnackbar", {
          msg: `Departure on ${this.$options.filters.formatDate(
            res.departure.startDate
          )} created.`,
        });
      } else {
        const created = (res.departures || []).length;
        const skipped = (res.skippedDates || []).length;
        this.$store.dispatch("showAppSnackbar", {
          msg: `${created} created${skipped ? `, ${skipped} skipped` : ""}.`,
        });
      }
      this.$apollo.queries.openGroupDepartures.refetch();
    },
  },

  apollo: {
    openGroupDepartures: {
      query: OPEN_GROUP_DEPARTURES_QUERY,
      variables() {
        return {
          after: this.after,
          first: this.first,
          filter: this.scopedFilter,
        };
      },
      skip() {
        return !this.openGroupId;
      },
      fetchPolicy: "network-only",
      result({ data }) {
        if (data && data.openGroupDepartures) {
          this.totalItems = data.openGroupDepartures.totalCount;
        }
      },
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/gt/open-groups/builder/OpenGroupDeparturesTab.vue